import { RainGaugeLocation } from '../../../models/location-card.model';
import { AutocompleteField, SelectField, TextField } from '../../models/field-models';
import { findFieldById, findSectionById, normalizeSeriesName } from '../base-utils';
// #61106 - hidden on Test and Prod until 07/09/2026 PRISM Release, unhide on 05/29/2026.
// import { AdvancedFieldId } from '../common.models';
import { AdvancedFieldId, BasicInfoCommonFieldIds, DataDeliveryFieldId, TAssembledApiData, TAssembledSmsRecipients } from '../common.models';
import {
    RAIN_CONFIGURATION_CONSTANT,
    RAIN_INSTALLATION_CONSTANT,
    RainAlarmFieldIds,
    RainInfoFieldIds,
    RainSensorFieldIds,
} from './base-config-models';
import { RainAlertLocationPayload, RainAlertConfigurationPayload } from './base-api-models';
import { EchoSmsRecipient, RainAlertLocationCardState, EchoEmailRecipient } from './models';
import { LocationStatus } from 'app/shared/models/locations';
import { S } from '@angular/cdk/keycodes';

export function gatherRainBasicInfoPayload(state: RainAlertLocationCardState): RainAlertLocationPayload[] {
    const mp1Info: Partial<RainAlertLocationPayload> = { ...RAIN_INSTALLATION_CONSTANT };

    Object.keys(SaveBasicInfoPayload).forEach((key) => {
        mp1Info[key] = SaveBasicInfoPayload[key](state);
    });

    return [mp1Info as RainAlertLocationPayload];
}

export const SaveBasicInfoPayload: { [key: string]: (state: RainAlertLocationCardState) => TAssembledApiData } = {
    locationName: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.LocationName)!.value,
    ipaddress: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.IpAddress)!.value,
    assignedRainGaugeId: (state) => {
        const rainGaugeField = findFieldById(state.formState, BasicInfoCommonFieldIds.AssignedRainGauge) as SelectField<
            RainGaugeLocation,
            string
        >;
        const rainGauge = rainGaugeField?.value;

        if (!rainGauge) {
            return null;
        }

        const assignedRainGauge = state.options.rainGauges.data.find((rg) => rg.locationName === rainGauge);

        if (!assignedRainGauge) {
            return null;
        }

        return assignedRainGauge.locationId;
    },
    communicationType: (state) =>
        Number(findFieldById(state.formState, BasicInfoCommonFieldIds.CommunicationType)!.value),
    description: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Description)!.value,
    installationDate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.InstallationDate)!.value,
    latitude: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Latitude)!.value,
    longitude: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Longitude)!.value,
    manholeaddress: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ManholeAddress)!.value,
    serialnumber: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SerialNumber)!.value,
    series: (state) => normalizeSeriesName(findFieldById(state.formState, BasicInfoCommonFieldIds.Series)!.value as string),
    status: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Status)!.value,
    maintenanceInterval: (state) => {
        const status = findFieldById(state.formState, BasicInfoCommonFieldIds.Status).value;
        return status === LocationStatus.Maintenance
            ? findFieldById(state.formState, BasicInfoCommonFieldIds.MaintenanceInterval)?.value ?? null
            : null;
    },
    apnProvider: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ApnProvider)!.value,
    samplerate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SampleRate)!.value,
    usedaylightsaving: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.UseDaylightSaving)!.value,
    synctime: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SyncTime)!.value,
    customProperties: (state) => {
        const customPropertiesSection = findSectionById(
            state.formState,
            BasicInfoCommonFieldIds.CustomPropertiesSection,
        );
        const customProperties: { key: string; value: string }[] = [];

        let i = 0;
        while (true) {
            if (customPropertiesSection!.fields[`${BasicInfoCommonFieldIds.CustomPropertyKey}-${i}`] === undefined) {
                break;
            }

            const keyField = customPropertiesSection!.fields[
                `${BasicInfoCommonFieldIds.CustomPropertyKey}-${i}`
            ] as AutocompleteField<string, string>;
            const valueField = customPropertiesSection!.fields[
                `${BasicInfoCommonFieldIds.CustomPropertyValue}-${i}`
            ] as TextField;

            customProperties.push({
                key: keyField.value,
                value: valueField.value,
            });

            i++;
        }

        return customProperties;
    },
};

export function gatherRainConfigurationInfo(state: RainAlertLocationCardState): RainAlertConfigurationPayload {

    const emailRec = [1,2,3,4,5].flatMap(i =>
        buildEmailRecipient(state, AdvancedFieldId[`CheckInEmailRecipient${i}`])
    );
    const smsRec = [1,2,3,4,5].flatMap(i =>
        buildSmsRecipient(state, {
            recipient: AdvancedFieldId[`CheckInTextRecipient${i}`],
            startHour: AdvancedFieldId[`CheckInTextRec${i}StartHour`],
            endHour: AdvancedFieldId[`CheckInTextRec${i}EndHour`],
        })
    );

    const result: { [key: string]: (state: RainAlertLocationCardState) => TAssembledApiData } = {
        prismdeliveryipaddress: (state) =>
        findFieldById(state.formState, DataDeliveryFieldId.PrismDeliveryIPAddress).value,
        prismdeliveryfast: (state) => findFieldById(state.formState, DataDeliveryFieldId.PrismDeliveryFast).value,
        prismdeliverynormal: (state) => findFieldById(state.formState, DataDeliveryFieldId.PrismDeliveryNormal).value,
        samplerate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SampleRate).value,
        usedaylightsaving: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.UseDaylightSaving).value,
        synctime: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SyncTime).value,
        apn: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ApnProvider).value,
        apnProvider: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ApnProvider).value,
        intensityinterval: (state) => findFieldById(state.formState, RainInfoFieldIds.IntensityInterval).value,
        logintensity: (state) => findFieldById(state.formState, RainInfoFieldIds.LogIntensity).value,
        logukintensity: (state) => findFieldById(state.formState, RainInfoFieldIds.LogUkIntensity).value,
        storebattery: (state) => findFieldById(state.formState, RainInfoFieldIds.StoreBattery).value,
        storetemperature: (state) => findFieldById(state.formState, RainInfoFieldIds.StoreTemperature).value,
        alarmEnable: (state) => findFieldById(state.formState, RainAlarmFieldIds.AlarmEnable).value,
        alarmThreshold: (state) => findFieldById(state.formState, RainAlarmFieldIds.AlarmThreshold).value,
        alarmReturnToNormal: (state) => findFieldById(state.formState, RainAlarmFieldIds.AlarmReturnToNormal).value,
        rainpertip: (state) => findFieldById(state.formState, RainSensorFieldIds.RainPerTip).value,
        datalogmode: (state) => findFieldById(state.formState, RainSensorFieldIds.DataLogMode).value,
        ipaddress: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.IpAddress)!.value,
        serialNumber: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SerialNumber)!.value,
        connectionType: (state) => Number(findFieldById(state.formState, BasicInfoCommonFieldIds.CommunicationType)!.value),
        checkinenable: (state) => findFieldById(state.formState, AdvancedFieldId.CheckInEnable).value,
        checkinhour: (state) => findFieldById(state.formState, AdvancedFieldId.CheckInHour).value,
        smsrecipients: () => smsRec.filter((v) => !!v),
        emailrecipients: () => emailRec.filter((v) => !!v)
    };

    const payload = Object.keys(result).reduce(
        (acc, key) => {
            acc[key] = result[key](state);
            return acc;
        },
        { ...RAIN_CONFIGURATION_CONSTANT },
    ) as RainAlertConfigurationPayload;

    if (payload.samplerate !== 120) {
        payload.logukintensity = false;
    }

    return payload;
}

function buildSmsRecipient(state: RainAlertLocationCardState,ids: {
        recipient: AdvancedFieldId;
        startHour: AdvancedFieldId;
        endHour: AdvancedFieldId;
    }
): EchoSmsRecipient[] {

    return [{
        recipient: findFieldById(state.formState, ids.recipient).value as string,
        startHour: Number(findFieldById(state.formState, ids.startHour).value),
        endHour: Number(findFieldById(state.formState, ids.endHour).value),
    }];
}



function buildEmailRecipient(state: RainAlertLocationCardState,id: AdvancedFieldId): EchoEmailRecipient[] {
        return [{
            recipient: findFieldById(state.formState, id).value as string
        }];
}

